import { useEffect, useMemo, useState } from 'react';
import type { ResolvedMasthead } from '@shared/types/Masthead.types';
import { resolveMasthead } from '@shared/constants/mastheads';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { decodeData } from '@shared/utils/encodeData';
import type { PreviewIframeMessage } from '@shared/utils/previewMessageBus';
import { previewMessageBusSubject, createPreviewMessageEventHandler } from '@shared/utils/previewMessageBus';
import { isInIframe } from '@shared/utils/isInIframe';
import globalWindow from '@shared/core/globals';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

// Structural subset of the generated EventDesign type — keeps this hook decoupled from codegen output.
type MastheadEventDesign = Readonly<{ masthead?: Maybe<Readonly<{ id?: string | null; isEnabled: boolean }>> }>;

/**
 * Website-designer live preview override. `undefined` = no message received yet (fall through
 * to the saved value); `null` = admin explicitly cleared the masthead.
 */
const usePreviewMastheadOverride = (): string | null | undefined => {
  const [override, setOverride] = useState<string | null | undefined>(undefined);

  const processMessage = useEventCallback((message: PreviewIframeMessage) => {
    // Template preview owns the masthead through eventDesign: PreviewListener swaps in the
    // payload's design (and restores its snapshot on reset), so drop this override instead of
    // letting an earlier designer selection shadow the previewed template's masthead.
    if (message.action === 'fromTemplatePreviewPayload' || message.action === 'resetTemplatePreview') {
      setOverride(undefined);
      return;
    }
    if (message.action !== 'fromList') {
      return;
    }
    message.value.forEach(item => {
      if (item.key === 'masthead') {
        setOverride(item.value);
      }
    });
  });

  useEffect(() => {
    if (!isInIframe()) {
      return;
    }
    const previewSub = previewMessageBusSubject.subscribe({
      next: action => {
        if (action.type === 'sendMessage') {
          processMessage(action.payload.message);
        }
      },
    });
    const receiveMessage = createPreviewMessageEventHandler(processMessage);
    globalWindow.addEventListener?.('message', receiveMessage, false);
    return () => {
      previewSub?.unsubscribe();
      globalWindow.removeEventListener?.('message', receiveMessage, false);
    };
  }, [processMessage]);

  return override;
};

/**
 * `decodeData` throws on a malformed blob and is a bare JSON.parse, so a well-formed blob can
 * still decode to a primitive. The decoded shape is load-bearing here (key presence decides
 * authority), so only hand back a real object.
 */
const decodeTemplateData = (templateData: unknown): { masthead?: string | null } | undefined => {
  if (typeof templateData !== 'string') {
    return undefined;
  }
  try {
    const decoded: unknown = decodeData(templateData);
    return typeof decoded === 'object' && decoded !== null ? (decoded as { masthead?: string | null }) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Single source of truth for where the mastheadId comes from.
 * Callers with event data pass their eventDesign; head/preview contexts without it may omit.
 */
export const useMasthead = (eventDesign?: Maybe<MastheadEventDesign>): ResolvedMasthead | null => {
  const [isEnabled] = useIsFeatureEnabled('websiteMastheadEnabled');
  const { templateData } = useQueryParams();
  // Enabled off → treat as no masthead, even though the BE retains the stored id.
  const savedMastheadId = eventDesign?.masthead?.isEnabled ? (eventDesign.masthead.id ?? null) : null;
  const previewOverride = usePreviewMastheadOverride();

  return useMemo(() => {
    if (!isEnabled) {
      return null;
    }
    // Source priority:
    // 1. templateData payload (admin template previews) — authoritative whenever it carries the
    //    `masthead` key, null included, so a template that ships none hides the saved masthead.
    //    A blob without the key (AI-stylist/MCP links, older preview URLs) falls through.
    // 2. website designer preview-bus override (null = explicitly cleared); yields to
    //    fromTemplatePreviewPayload, which routes through eventDesign instead.
    // 3. eventDesign.masthead saved via BE setMasthead/setMastheadEnabled — also how the
    //    template preview payload's masthead arrives.
    const decodedTemplateData = decodeTemplateData(templateData);

    const mastheadId =
      decodedTemplateData && 'masthead' in decodedTemplateData ? (decodedTemplateData.masthead ?? null) : previewOverride !== undefined ? previewOverride : savedMastheadId;

    return resolveMasthead(mastheadId);
  }, [isEnabled, templateData, previewOverride, savedMastheadId]);
};
